import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import AvatarBadge from "./components/avatarBadge";

// Hooks
import useNotifications from "hooks/useNotifications";

// Components
import Breadcrumbs from "common/Breadcrumbs";
import NotificationItem from "common/NotificationItem";
import NotificationAlert from "common/NotificationAlert";
import CalendarPreview from "./components/calendarPreview";

// Custom styles for DashboardNavbar
import styles from "common/Navbars/DashboardNavbar/styles";

// Context
import { useSoftUIController } from "context";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const history = useHistory();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const classes = styles({ transparentNavbar, absolute, light, isMini });
  const route = useLocation().pathname.split("/").slice(1);

  // Notifications hook con estado del socket
  const { notifications, unreadCount, loading, handleMarkAsRead, socketConnected } =
    useNotifications();

  // Efecto para manejar el navbar transparente
  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    const handleTransparentNavbar = () => {
      dispatch({
        type: "TRANSPARENT_NAVBAR",
        value: (fixedNavbar && window.scrollY === 0) || !fixedNavbar,
      });
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Efecto para manejar las alertas cuando hay nuevas notificaciones
  useEffect(() => {
    if (notifications.length > 0) {
      const lastNotification = notifications[0];
      if (!lastNotification.read) {
        setAlerts((prev) => {
          // Evitar duplicados
          if (!prev.includes(lastNotification.message)) {
            return [...prev, lastNotification.message];
          }
          return prev;
        });
      }
    }
  }, [notifications]);

  const handleMiniSidenav = () => dispatch({ type: "MINI_SIDENAV", value: !miniSidenav });
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleNotificationHover = async (notificationId) => {
    try {
      await handleMarkAsRead(notificationId);
    } catch (error) {
      console.error("Error al marcar la notificación como leída:", error);
    }
  };

  const removeAlert = (index) => {
    setAlerts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewAll = () => {
    history.push("/notifications");
    handleCloseMenu();
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      style={{ marginTop: "1rem" }}
    >
      {loading ? (
        <MenuItem>
          <SuiTypography variant="button" textColor="secondary" fontWeight="regular">
            Cargando notificaciones...
          </SuiTypography>
        </MenuItem>
      ) : notifications.length > 0 ? (
        [
          ...notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              id={notification.id}
              color={notification.color}
              icon={notification.icon}
              title={notification.title}
              message={notification.message}
              timeAgo={notification.time_ago}
              read={notification.read}
              onMarkAsRead={handleNotificationHover}
              sx={{
                backgroundColor: notification.read ? "transparent" : "action.hover",
                "&:hover": {
                  backgroundColor: "action.selected",
                },
              }}
            />
          )),
          <MenuItem
            key="view-all"
            onClick={handleViewAll}
            sx={{
              justifyContent: "center",
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 1,
            }}
          >
            <SuiTypography
              variant="button"
              textColor="info"
              fontWeight="medium"
              customClass={classes.navbar_menu_item}
            >
              Ver todas las notificaciones
            </SuiTypography>
          </MenuItem>,
        ]
      ) : (
        <MenuItem>
          <SuiTypography variant="button" textColor="secondary" fontWeight="regular">
            No hay notificaciones
          </SuiTypography>
        </MenuItem>
      )}
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      className={classes.navbar}
    >
      <Toolbar className={classes.navbar_container}>
        <SuiBox customClass={classes.navbar_row} color="inherit" mb={{ xs: 1, md: 0 }}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SuiBox>
        {isMini ? null : (
          <SuiBox customClass={classes.navbar_row}>
            <SuiBox
              color={light ? "white" : "inherit"}
              customClass={classes.navbar_section_desktop}
            >
              <IconButton
                size="small"
                color="inherit"
                className={classes.navbar_mobile_menu}
                onClick={handleMiniSidenav}
              >
                <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
              </IconButton>
              <IconButton
                color="inherit"
                className={classes.navbar_icon_button}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <Icon className={!socketConnected ? "blink" : ""}>notifications</Icon>
                </Badge>
              </IconButton>

              {renderMenu()}

              <CalendarPreview />

              <AvatarBadge />
            </SuiBox>
          </SuiBox>
        )}
      </Toolbar>
      <SuiBox
        sx={{
          position: "fixed",
          top: 72,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          zIndex: 1200,
        }}
      >
        {alerts.map((alert, index) => (
          <NotificationAlert
            key={index}
            message={alert}
            onClose={() => removeAlert(index)}
            slideDirection="right"
          />
        ))}
      </SuiBox>
    </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
